<template>
  <div v-if="isLoading">
    <AddressBlockTronSkeleton />
  </div>
  <div v-else>
    <div class="address-block_header">
      <Text pattern="body1" :weight="600" height="150%" color="#fafafa">
        {{ t("Main") }}
      </Text>
    </div>
    <div class="address-block">
      <div class="address-block_wrapper">
        <div class="address-block_left">
          <Text pattern="body3" :weight="500" height="150%" color="#7a7a7a">
            {{ t("From") }}
          </Text>
          <div v-if="!isInstantLink" class="address-block_address_row">
            <router-link
              v-if="addressFrom && network"
              :to="{
                name: 'Address Details',
                params: { network: network, id: addressFrom },
              }"
              class="address-block_address underline hover:text-color-6 transition-colors cursor-pointer link"
            >
              {{ shorten(addressFrom) }}
            </router-link>
            <span v-else class="address-block_address">
              {{ addressFrom ? shorten(addressFrom) : "N/A" }}
            </span>
            <BaseSvgIcon
              name="copy"
              class="address-block_copy"
              @click="onCopyAddress(addressFrom)"
            />
          </div>
          <BaseSkeleton
            v-if="isInstantLink"
            class="address-block_from-skeleton"
            isNoAnimation
          />
        </div>
        <div class="address-block_right">
          <Text pattern="body3" :weight="500" height="150%" color="#7a7a7a">
            {{ t("To") }}
          </Text>
          <div class="address-block_address_row">
            <router-link
              v-if="addressTo && network"
              :to="{
                name: 'Address Details',
                params: { network: network, id: addressTo },
              }"
              class="address-block_address underline hover:text-color-6 transition-colors cursor-pointer link"
            >
              {{ shorten(addressTo) }}
            </router-link>
            <span v-else class="address-block_address">
              {{ addressTo ? shorten(addressTo) : "N/A" }}
            </span>
            <BaseSvgIcon
              name="copy"
              class="address-block_copy"
              @click="onCopyAddress(addressTo)"
            />
          </div>
        </div>
      </div>
      <template v-if="hasAmountData">
        <div class="address-block_amount_main">
          <Text pattern="body3" :weight="500" height="150%" color="#7a7a7a">
            {{ $t("Amount") }}
          </Text>
          <div class="address-block_amount_row">
            <div class="address-block_amount" :class="amountClass">
              <BaseSvgIcon class="address-block_amount_icon" name="confirmed" />
              <span class="address-block_amount_title">
                {{ displaySign
                }}{{ formatBalance(props.amount, props.decimals) }}
                {{ props.symbol }}
              </span>
              <span v-if="formattedUsdValue" class="address-block_amount_title">
                &nbsp;•&nbsp; {{ formattedUsdValue }}
              </span>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance } from "vue";
import { useI18n } from "vue-i18n";
import { Text } from "@/shared/ui";
import {
  // first36symbols,
  // first25symbols,
  shorten,
  amount as formatBalance,
} from "@/shared/utils/format";
import { copyToClipboard } from "@emcd/common/helpers";
import BaseSvgIcon from "@/shared/ui/BaseSvgIcon/BaseSvgIcon.vue";
import "@/assets/icons/SVG/copy.svg";
import "@/assets/icons/SVG/circle_arrow_left.svg";
import "@/assets/icons/SVG/confirmed.svg";
import AddressBlockTronSkeleton from "./AddressBlockTronSkeleton.vue";
import { formatFiat } from "@/shared/utils/formaters/transactionFormatter";
import BaseSkeleton from "../../../../shared/ui/BaseSkeleton/BaseSkeleton.vue";

// Props
const props = defineProps({
  addressFrom: {
    type: String,
    required: true,
  },
  addressTo: {
    type: String,
    required: true,
  },
  amount: {
    type: Number,
    required: true,
  },
  decimals: {
    type: Number,
    required: true,
  },
  symbol: {
    type: String,
    required: true,
  },
  usd_value: {
    type: String,
    required: true,
  },
  network: {
    type: String,
    required: true,
  },
  showAmountSection: {
    type: Boolean,
    default: true,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  isInstantLink: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const { proxy } = getCurrentInstance();

// Methods
const onCopyAddress = (address) => {
  copyToClipboard(address, () => {});
  proxy.$toast({
    type: "success",
    title: t("AddressCopied"),
  });
};

// Computed properties
const hasAmountData = computed(() => {
  return props.amount && props.showAmountSection;
});

const formattedUsdValue = computed(() => {
  if (
    !hasAmountData.value ||
    props.usd_value === null ||
    props.usd_value === undefined
  )
    return "";
  return formatFiat(props.usd_value);
});
</script>

<style lang="scss" scoped>
.address-block {
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  border: 1px solid var(--Dark-850, #1a1a1a);
  padding: 20px 24px;

  &_right {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 42px;
  }

  &_address_row:has(+ .address-block_amount_row:not(:last-child)) {
    margin-bottom: 15px;
  }

  &_address_row:not(:has(+ .address-block_amount_row)) {
    margin-bottom: 0;
  }

  &_header {
    margin-bottom: 16px;
  }

  &_divider {
    margin-bottom: 16px;
    border-bottom: 1px solid var(--Dark-850, #1a1a1a);
  }

  &_address {
    // width: 100%;
    overflow: hidden;
    color: #fff;
    text-overflow: ellipsis;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 130%;

    @media (max-width: 768px) {
      font-size: 13px;
    }

    &_row {
      display: flex;
      align-items: center;
      // justify-content: space-between;
      gap: 8px;
    }
  }

  &_wrapper {
    display: flex;
    // justify-content: space-between;
    align-items: center;

    @media (max-width: 768px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 16px;
    }
  }

  &_left {
    width: 50%;

    @media (max-width: 768px) {
      width: 100%;
    }
  }

  &_right {
    width: 50%;

    @media (max-width: 768px) {
      width: 100%;
    }
  }

  &_amount_main {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 42px;
    margin-top: 16px;
  }

  &_amount {
    font-size: 14px;
    font-weight: 500;
    line-height: 1.4;
    color: #fafafa;
    display: flex;
    align-items: center;
    gap: 4px;

    &.neutral {
      color: #fafafa;
    }

    &_row {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    &_title {
      line-height: 150%;
    }

    &_icon {
      width: 16px;
      height: 16px;
    }
  }

  &_copy {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    transition: color 0.3s;
    cursor: pointer;
    filter: brightness(0) saturate(100%) invert(76%) sepia(48%) saturate(5896%)
      hue-rotate(220deg) brightness(104%) contrast(98%);

    &:hover {
      filter: brightness(0) saturate(100%) invert(31%) sepia(96%)
        saturate(3271%) hue-rotate(251deg) brightness(100%) contrast(103%);
    }
  }

  &_from-skeleton {
    width: 300px;
    height: 18.2px;
    border-radius: 10px;

    @media (max-width: 768px) {
      width: 220px;
    }
  }
}
</style>
